import { useState, useEffect, useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { Link, useNavigate } from 'react-router-dom';
import Carousel from '../../../assets/carousel';
import useAuth from '../../../hooks/useAuth';
import api from '../../../api/axios';
import { useLoading } from '../../../components/LoadingProvider';
import { showError } from '../../../utils/toast';
import { getPublicImgUrl } from '../../../utils/common';

function ClinicDashboard() {
    const navigate = useNavigate();
    const { user, isAuthenticated, userRole } = useAuth({ redirectPath: '/login', allowedRoles: ['clinic'] });
    const [doctorList, setDoctorList] = useState([]);
    const [specializations, setSpecializations] = useState([]);
    const [doctorSearchText, setDoctorSearchText] = useState('');
    const { setIsLoading } = useLoading();
    const doctorSearch = useRef(null);
    const handleScrollToDoctorSearch = () => {
        doctorSearch.current?.scrollIntoView({ behavior: 'smooth' });
    };
    const debounced = useDebouncedCallback(async (value) => {
        setIsLoading(true);
        setDoctorList([]);
        try {
            setDoctorSearchText(value);
            const response = await api.get('/doctor/search?text='+value);
            if (response?.data?.result === 'success') {
                setDoctorList(response?.data?.data?.doctors || []);
            }else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError(response.data.message);
            }
        } catch (error) {
            showError(error.message);
        } finally {
            setIsLoading(false);
        }
    }, 1000);

    useEffect(() => {
        document.title = 'Varosa - Clinic Dashboard';
        
        // Check authentication status
        if (!isAuthenticated || !user) {
            navigate('/login');
            return;
        }
    }, [isAuthenticated, user, navigate]);

    useEffect(() => {
        document.title = 'Varosa - Home';
        // Initialize carousel
        const initCarousel = () => {
            const carouselElement = document.getElementById('headerCarousel');
            if (carouselElement) {
                const carousel = new Carousel(carouselElement);

                // Add button controls
                const prevBtn = document.getElementById('prevBtn');
                const nextBtn = document.getElementById('nextBtn');

                if (prevBtn && nextBtn) {
                    prevBtn.addEventListener('click', () => {
                        carousel.stopAutoPlay();
                        carousel.prevSlide();
                        carousel.startAutoPlay();
                    });

                    nextBtn.addEventListener('click', () => {
                        carousel.stopAutoPlay();
                        carousel.nextSlide();
                        carousel.startAutoPlay();
                    });
                }
            }
        };
        const initTestimonialCarousel = () => {
            const carouselElement = document.getElementById('testimonialCarousel');
            if (carouselElement) {
                const carousel = new Carousel(carouselElement);

                // Add button controls
                const prevBtn = document.getElementById('prevBtnT');
                const nextBtn = document.getElementById('nextBtnT');

                if (prevBtn && nextBtn) {
                    prevBtn.addEventListener('click', () => {
                        carousel.stopAutoPlay();
                        carousel.prevSlide();
                        carousel.startAutoPlay();
                    });

                    nextBtn.addEventListener('click', () => {
                        carousel.stopAutoPlay();
                        carousel.nextSlide();
                        carousel.startAutoPlay();
                    });
                }
            }
        };

        initCarousel();
        initTestimonialCarousel();
        // Get specializations
        api.get(`/${userRole}/dashboard`).then((response) => {
            if (response?.data?.result === 'success') {
                setSpecializations((response?.data?.data?.specializations || []).filter(item => item.in_top_list));
                setDoctorList(response?.data?.data?.top_doctors || []);
            }else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError(response.data?.message || 'An error occurred');
            }
        }).catch((error) => {
            showError(error.message);
        });
    }, []);

    return (
        <>
            <div className="container-fluid py-3">
                <div className="container">
                    <div className="row">
                        <div className="col-12">
                            <div className="bg-light rounded p-4">
                                <h2 className="mb-0">Welcome, {user?.name || 'User'}!</h2>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="container-fluid position-relative p-0">
                <div className="header-carousel" id="headerCarousel">
                    <div className="header-carousel-item">
                        <img src={getPublicImgUrl()+"/banner-1.png"} className="img-fluid w-100 cursor-pointer" alt="img" onClick={handleScrollToDoctorSearch} />
                        {/* <div className="carousel-caption">
                            <div className="carousel-caption-content p-3">
                                <a className="book-btn" href="#findDoctor">Book Appointment</a>
                            </div>
                        </div> */}
                    </div>
                    <div className="header-carousel-item">
                        <img src={getPublicImgUrl()+"/banner-2.png"} className="img-fluid w-100 cursor-pointer" alt="img" onClick={handleScrollToDoctorSearch} />
                        {/* <div className="carousel-caption">
                            <div className="carousel-caption-content p-3">
                                <a className="btn btn-primary rounded-pill text-white py-3 px-5" href="#findDoctor">Book Appointment</a>
                            </div>
                        </div> */}
                    </div>
                    <div className="header-carousel-item">
                        <img src={getPublicImgUrl()+"/banner-3.png"} className="img-fluid w-100 cursor-pointer" alt="img" onClick={handleScrollToDoctorSearch} />
                        {/* <div className="carousel-caption">
                            <div className="carousel-caption-content p-3">
                                <a className="btn btn-primary rounded-pill text-white py-3 px-5" href="#findDoctor">Book Appointment</a>
                            </div>
                        </div> */}
                    </div>
                    <div className="owl-nav">
                        <div className="owl-prev" id="prevBtn"><i className="bi bi-arrow-left"></i></div>
                        <div className="owl-next" id="nextBtn"><i className="bi bi-arrow-right"></i></div>
                    </div>
                </div>
            </div>
            <div className="container-fluid service py-5">
                <div className="container py-2">
                    <div className="section-title mb-5 wow fadeInUp" data-wow-delay="0.2s">
                        <div className="sub-style">
                            <h4 className="sub-title px-3 mb-0">Specialities</h4>
                        </div>
                        <h1 className="display-3 mb-4">Our Service Given By Expert.</h1>
                        <p className="mb-0"></p>
                    </div>
                    <div className="row g-4 justify-content-center">
                        { specializations.map((item, index, arr) => (
                            <div className="col-md-6 col-lg-4 col-xl-3 wow fadeInUp" data-wow-delay="0.1s" key={item?.slug}>
                                <div className="service-item rounded">
                                    <div className="service-img rounded-top">
                                    <img src={item?.image} className="img-fluid rounded-top w-100" alt="" />
                                </div>
                                <div className="service-content rounded-bottom bg-light p-4">
                                    <div className="service-content-inner">
                                        <h5 className="mb-4">{item?.name || 'Period doubts or Pregnancy'}</h5>
                                        <p className="mb-4"></p>
                                        <Link to={`/specialization/${item?.slug}/doctors`} className="btn btn-primary rounded-pill text-white py-2 px-4 mb-2">FIND DOCTORS</Link>
                                    </div>
                                </div>
                            </div>
                        </div>))}
                        <div className="col-12 text-center">
                            <Link to="/specialization/all" className="btn btn-primary rounded-pill text-white py-3 px-5">See all Specialities</Link>
                        </div>
                    </div>
                </div>
            </div>
            <div className="container-fluid service bg-light py-5" id="findDoctor" ref={doctorSearch}>
                <div className="container">
                    <div className="section-title mb-5 wow fadeInUp" data-wow-delay="0.2s">
                        <div className="sub-style">
                            <h4 className="sub-title px-3 mb-0">Find A Doctor</h4>
                        </div>
                    </div>
                    <div className="row g-4 justify-content-center">
                        <div className="col-md-6 text-center col-sm-12">
                            <form>
                                <div className="input-group mb-3" title="Search by Doctor Name / Department / Symptoms">
                                    <input type="text" className="form-control py-3 border-primary bg-transparent" placeholder="Search by Doctor Name / Department / Symptoms" onChange={(e) => {
                                        debounced(e.target.value);
                                    }} />
                                    <button type="button" className="btn btn-primary text-white py-1 px-3">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-search" viewBox="0 0 16 16">
                                            <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0"/>
                                        </svg>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                    {doctorList.length > 0 ? (
                        <div className="row g-4 mt-2 justify-content-center">
                            {doctorList.map((doctor) => (
                                <div className="col-md-4 col-sm-6 col-xs-12 wow fadeInUp" data-wow-delay="0.1s" key={doctor.id}>
                                    <div className="team-item rounded">
                                        <div className="team-img rounded-top h-100 border border-primary border-bottom-0 pt-3">
                                            <img src={doctor.profile_picture || (getPublicImgUrl()+'/doctor_2785482.png')} className="img-fluid rounded-top w-100" style={{ maxHeight: '250px', objectFit: 'contain' }} alt="" />
                                        </div>
                                        <div className="team-content text-center border border-primary border-top-0 rounded-bottom p-4">
                                            <h5 className="mb-2">{doctor.name}</h5>
                                            {doctor.qualification && (<p className="mb-3">
                                                <b>Qualification: </b> 
                                                {doctor.qualification?.length > 100 ? doctor.qualification.slice(0, 100) : doctor.qualification}
                                                {doctor.qualification?.length > 100 ? (
                                                    <>
                                                    <span className={`d-none rest-qualification-${doctor.id}`}>{doctor.qualification.slice(100)}</span>
                                                    &nbsp;&nbsp;
                                                    <button onClick={(e) => { 
                                                        e.preventDefault();
                                                        var elements = document.getElementsByClassName(`rest-qualification-${doctor.id}`);
                                                        if(elements.length > 0){
                                                            if(elements[0].classList.contains("d-none")){
                                                                elements[0].classList.remove("d-none");
                                                                e.target.textContent = 'Read Less';
                                                            }else{
                                                                elements[0].classList.add("d-none");
                                                                e.target.textContent = 'Read More';
                                                            }
                                                        }
                                                    }} className="btn btn-outline-success btn-sm ml-3">Read More</button>
                                                    </>
                                                ) : ''}
                                            </p>)}
                                            {doctor.bio && (<p className="mb-3">
                                                <b>Bio:</b> {doctor.bio?.length > 100 ? doctor.bio.slice(0, 100) : doctor.bio}
                                                {doctor.bio?.length > 100 ? (
                                                    <>
                                                    <span className={`d-none rest-bio-${doctor.id}`}>{doctor.bio.slice(100)}</span>
                                                    &nbsp;&nbsp;
                                                    <button onClick={(e) => { 
                                                        e.preventDefault();
                                                        var elements = document.getElementsByClassName(`rest-bio-${doctor.id}`);
                                                        if(elements.length > 0){
                                                            if(elements[0].classList.contains("d-none")){
                                                                elements[0].classList.remove("d-none");
                                                                e.target.textContent = 'Read Less';
                                                            }else{
                                                                elements[0].classList.add("d-none");
                                                                e.target.textContent = 'Read More';
                                                            }
                                                        }
                                                    }} className="btn btn-outline-success btn-sm ml-3">Read More</button>
                                                    </>
                                                ) : ''}
                                            </p>)}
                                            <p className="mb-3"><b>Specializations:</b> {doctor.specializations.map((specialization) => (
                                                <span key={specialization} className="badge bg-primary me-1">{specialization}</span>
                                            ))}</p>
                                            {doctor.symptoms && <p className="mb-3"><b>Symptoms Treated:</b> {doctor.symptoms}</p>}
                                            <p className="mb-3"><b>Consultation Fees: <i>&#8377;{doctor.consultation_fee}</i></b></p>
                                            <hr />
                                            <button onClick={(e) => { 
                                                e.preventDefault();
                                                navigate(`/dashboard/clinic/appointment/book/?t=${doctorSearchText}&d=${doctor.id}`)
                                            }} className="btn btn-primary text-white">Book Appointment</button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="row g-4 justify-content-center">
                            <div className="col-md-6 text-center col-sm-12">
                                <h5 className="mb-1 mt-1 py-3 text-center border border-primary rounded">
                                    <i className="fas fa-exclamation-circle me-2"></i>
                                    No Doctor Found
                                </h5>
                            </div>
                        </div>
                    )}
                    <div className="row g-4 mt-2 justify-content-center d-none" style={{ display: 'none' }}>
                        <div className="col-md-6 col-lg-4 col-xl-3 wow fadeInUp" data-wow-delay="0.1s">
                            <div className="team-item rounded">
                                <div className="team-img rounded-top h-100">
                                    <img src={getPublicImgUrl()+"/team-1.jpg"} className="img-fluid rounded-top w-100" alt="" />
                                </div>
                                <div className="team-content text-center border border-primary border-top-0 rounded-bottom p-4">
                                    <h5>Full Name</h5>
                                    <p className="mb-0">Message Physio Therapist</p>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4 col-xl-3 wow fadeInUp" data-wow-delay="0.1s">
                            <div className="team-item rounded">
                                <div className="team-img rounded-top h-100">
                                    <img src={getPublicImgUrl()+"/team-2.jpg"} className="img-fluid rounded-top w-100" alt="" />
                                </div>
                                <div className="team-content text-center border border-primary border-top-0 rounded-bottom p-4">
                                    <h5>Full Name</h5>
                                    <p className="mb-0">Rehabilitation Therapist</p>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4 col-xl-3 wow fadeInUp" data-wow-delay="0.1s">
                            <div className="team-item rounded">
                                <div className="team-img rounded-top h-100">
                                    <img src={getPublicImgUrl()+"/team-3.jpg"} className="img-fluid rounded-top w-100" alt="" />
                                </div>
                                <div className="team-content text-center border border-primary border-top-0 rounded-bottom p-4">
                                    <h5>Full Name</h5>
                                    <p className="mb-0">Doctor of Physical therapy</p>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4 col-xl-3 wow fadeInUp" data-wow-delay="0.1s">
                            <div className="team-item rounded">
                                <div className="team-img rounded-top h-100">
                                    <img src={getPublicImgUrl()+"/team-4.jpg"} className="img-fluid rounded-top w-100" alt="" />
                                </div>
                                <div className="team-content text-center border border-primary border-top-0 rounded-bottom p-4">
                                    <h5>Full Name</h5>
                                    <p className="mb-0">Doctor of Physical therapy</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="container-fluid feature py-5">
                <div className="container py-2">
                    <div className="section-title mb-5 wow fadeInUp" data-wow-delay="0.1s">
                        <div className="sub-style">
                            <h4 className="sub-title px-3 mb-0">Why Choose Us</h4>
                        </div>
                        <h1 className="display-3 mb-4">Why Choose Us? Get Your Life Style Back</h1>
                        {/* <p className="mb-0">At Varosa Healthcare, we bring quality medical care directly to your fingertips. Our telemedicine platform connects you with experienced healthcare professionals anytime, anywhere—ensuring you receive timely consultation without the hassle of travel or long waiting hours.</p> */}
                        <p className="mb-0">Choosing Varosa means choosing convenience, reliability, and modern healthcare solutions. With its clinic-focused approach and advanced telemedicine platform, Varosa ensures that quality medical care is always within reach.</p>
                    </div>
                    <div className="row g-4 justify-content-center">
                        <div className="col-md-6 col-lg-4 col-xl-3 wow fadeInUp" data-wow-delay="0.1s">
                            <div className="row-cols-1 feature-item p-4">
                                <div className="col-12">
                                    <div className="feature-icon mb-4">
                                        <div className="p-3 d-inline-flex bg-white rounded">
                                            <img className="img-fluid rounded-top w-100" alt="" src={getPublicImgUrl()+"/online-doctor.png"} />
                                        </div>
                                    </div>
                                    <div className="feature-content d-flex flex-column">
                                        <h5 className="mb-4">Easy Access to Doctors</h5>
                                        <p className="mb-0">Varosa allows patients to consult qualified doctors from the comfort of their home. No need to travel long distances or wait in crowded clinics.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4 col-xl-3 wow fadeInUp" data-wow-delay="0.5s">
                            <div className="row-cols-1 feature-item p-4">
                                <div className="col-12">
                                    <div className="feature-icon mb-4">
                                        <div className="p-3 d-inline-flex bg-white rounded">
                                            <img className="img-fluid rounded-top w-100" alt="" src={getPublicImgUrl()+"/Support.png"} />
                                        </div>
                                    </div>
                                    <div className="feature-content d-flex flex-column">
                                        <h5 className="mb-4">24/7 Medical Support</h5>
                                        <p className="mb-0">Healthcare needs can arise anytime. Varosa provides round-the-clock access to medical consultations so you can get help whenever you need it.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4 col-xl-3 wow fadeInUp" data-wow-delay="0.7s">
                            <div className="row-cols-1 feature-item p-4">
                                <div className="col-12">
                                    <div className="feature-icon mb-4">
                                        <div className="p-3 d-inline-flex bg-white rounded">
                                            <img className="img-fluid rounded-top w-100" alt="" src={getPublicImgUrl()+"/save-time.png"} />
                                        </div>
                                    </div>
                                    <div className="feature-content d-flex flex-column">
                                        <h5 className="mb-4">Time-Saving Service</h5>
                                        <p className="mb-0">With telemedicine, patients can avoid long waiting hours at hospitals and get quick consultations through their phone or computer.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4 col-xl-3 wow fadeInUp" data-wow-delay="0.1s">
                            <div className="row-cols-1 feature-item p-4">
                                <div className="col-12">
                                    <div className="feature-icon mb-4">
                                        <div className="p-3 d-inline-flex bg-white rounded">
                                            <img className="img-fluid rounded-top w-100" alt="" src={getPublicImgUrl()+"/affortable.png"} />
                                        </div>
                                    </div>
                                    <div className="feature-content d-flex flex-column">
                                        <h5 className="mb-4">Affordable Healthcare</h5>
                                        <p className="mb-0">Varosa offers cost-effective consultation services compared to traditional hospital visits, making healthcare more accessible to everyone.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4 col-xl-3 wow fadeInUp" data-wow-delay="0.3s">
                            <div className="row-cols-1 feature-item p-4">
                                <div className="col-12">
                                    <div className="feature-icon mb-4">
                                        <div className="p-3 d-inline-flex bg-white rounded">
                                            <img className="img-fluid rounded-top w-100" alt="" src={getPublicImgUrl()+"/private consultation.png"} />
                                        </div>
                                    </div>
                                    <div className="feature-content d-flex flex-column">
                                        <h5 className="mb-4">Secure and Private Consultations</h5>
                                        <p className="mb-0">Patient privacy is a top priority. Varosa ensures secure communication and confidential handling of medical information.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4 col-xl-3 wow fadeInUp" data-wow-delay="0.5s">
                            <div className="row-cols-1 feature-item p-4">
                                <div className="col-12">
                                    <div className="feature-icon mb-4">
                                        <div className="p-3 d-inline-flex bg-white rounded">
                                            <img className="img-fluid rounded-top w-100" alt="" src={getPublicImgUrl()+"/digital-prescription.png"} />
                                        </div>
                                    </div>
                                    <div className="feature-content d-flex flex-column">
                                        <h5 className="mb-4">Digital Prescriptions</h5>
                                        <p className="mb-0">After consultation, patients receive digital prescriptions instantly, which can be used at pharmacies without delay.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4 col-xl-3 wow fadeInUp" data-wow-delay="0.7s">
                            <div className="row-cols-1 feature-item p-4">
                                <div className="col-12">
                                    <div className="feature-icon mb-4">
                                        <div className="p-3 d-inline-flex bg-white rounded">
                                            <img className="img-fluid rounded-top w-100" alt="" src={getPublicImgUrl()+"/follow up.png"} />
                                        </div>
                                    </div>
                                    <div className="feature-content d-flex flex-column">
                                        <h5 className="mb-4">Follow-Up Care Made Simple</h5>
                                        <p className="mb-0">Patients can easily schedule follow-up consultations with their doctors to monitor their health progress.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4 col-xl-3 wow fadeInUp" data-wow-delay="0.3s">
                            <div className="row-cols-1 feature-item p-4">
                                <div className="col-12">
                                    <div className="feature-icon mb-4">
                                        <div className="p-3 d-inline-flex bg-white rounded">
                                            <img className="img-fluid rounded-top w-100" alt="" src={getPublicImgUrl()+"/accessibility.png"} />
                                        </div>
                                    </div>
                                    <div className="feature-content d-flex flex-column">
                                        <h5 className="mb-4">Accessible from Anywhere</h5>
                                        <p className="mb-0">Whether you are at home, at work, or traveling, Varosa enables you to connect with doctors anytime using your smartphone or computer.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-12 text-center wow fadeInUp" data-wow-delay="0.2s">
                            <Link to="/#findDoctor" className="btn btn-primary rounded-pill text-white py-3 px-5">More Details</Link>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default ClinicDashboard;